import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface PaymentDashboardProps {
  onViewInvoice: (invoice: any) => void;
  onAddPayment: (invoice: any) => void;
}

interface Invoice {
  id: number;
  numero_facture_fournisseur: string;
  fournisseur: {
    id: number;
    raison_sociale: string;
  };
  date_facture: string;
  date_echeance: string | null;
  total_ttc: number;
  montant_paye: number;
  montant_restant: number;
  statut_paiement_label: string;
  jours_avant_echeance: number | null;
  en_retard: boolean;
  alerte_echeance: boolean;
  bonsReception: any[];
  paiements: any[];
}

export default function PaymentDashboard({ onViewInvoice, onAddPayment }: PaymentDashboardProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [sortBy, setSortBy] = useState('echeance');

  // Charger les factures depuis l'API
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await getResources('factures-fournisseur'); // Adaptez l'URL si nécessaire
        if (response.data.success) {
          setInvoices(response.data.data);
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Erreur lors du chargement des factures');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getUrgencyLevel = (invoice: Invoice) => {
    if (invoice.en_retard) return 'retard';
    if (invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance <= 3) return 'urgent';
    if (invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance <= 7) return 'attention';
    return 'normal';
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'retard': return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'attention': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyIcon = (level: string) => {
    switch (level) {
      case 'retard': return 'ri-alarm-warning-fill';
      case 'urgent': return 'ri-time-line';
      case 'attention': return 'ri-calendar-line';
      default: return 'ri-calendar-check-line';
    }
  };

  // Filtrer les factures non soldées
  const unpaidInvoices = invoices.filter(invoice => 
    invoice.statut_paiement_label !== 'Soldée'
  );

  // Filtrer par période
  const filteredInvoices = unpaidInvoices.filter(invoice => {
    if (filterPeriod === 'all') return true;

    if (filterPeriod === 'overdue') return invoice.en_retard;
    if (filterPeriod === 'week') return invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance >= 0 && invoice.jours_avant_echeance <= 7;
    if (filterPeriod === 'month') return invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance >= 0 && invoice.jours_avant_echeance <= 30;

    return true;
  });

  // Trier les factures
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    switch (sortBy) {
      case 'echeance':
        const dateA = a.date_echeance ? new Date(a.date_echeance).getTime() : Infinity;
        const dateB = b.date_echeance ? new Date(b.date_echeance).getTime() : Infinity;
        return dateA - dateB;
      case 'montant':
        return b.montant_restant - a.montant_restant;
      case 'fournisseur':
        return a.fournisseur.raison_sociale.localeCompare(b.fournisseur.raison_sociale);
      default:
        return 0;
    }
  });

  // Statistiques
  const stats = {
    total: unpaidInvoices.length,
    enRetard: unpaidInvoices.filter(inv => inv.en_retard).length,
    urgent: unpaidInvoices.filter(inv => inv.jours_avant_echeance !== null && inv.jours_avant_echeance >= 0 && inv.jours_avant_echeance <= 3).length,
    attention: unpaidInvoices.filter(inv => inv.jours_avant_echeance !== null && inv.jours_avant_echeance > 3 && inv.jours_avant_echeance <= 7).length,
    montantTotal: unpaidInvoices.reduce((sum, inv) => sum + inv.montant_restant, 0),
    montantRetard: unpaidInvoices.filter(inv => inv.en_retard).reduce((sum, inv) => sum + inv.montant_restant, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des factures...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques des échéances */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total à Payer</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Retard</p>
              <p className="text-2xl font-bold text-red-600">{stats.enRetard}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-alarm-warning-fill text-xl text-red-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Urgent (≤3j)</p>
              <p className="text-2xl font-bold text-orange-600">{stats.urgent}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-xl text-orange-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Attention (≤7j)</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.attention}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-line text-xl text-yellow-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Montant Total</p>
              <p className="text-lg font-bold text-blue-600">{formatAmount(stats.montantTotal)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Retard</p>
              <p className="text-lg font-bold text-red-600">{formatAmount(stats.montantRetard)}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-red-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="all">Toutes les échéances</option>
                <option value="overdue">En retard</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trier par</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="echeance">Date d'échéance</option>
                <option value="montant">Montant</option>
                <option value="fournisseur">Fournisseur</option>
              </select>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            {filteredInvoices.length} facture(s) affichée(s)
          </div>
        </div>
      </div>

      {/* Alerte retard */}
      {stats.enRetard > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <i className="ri-alarm-warning-fill text-2xl text-red-600 mt-1"></i>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">Factures en Retard de Paiement</h4>
              <p className="text-sm text-red-800">
                {stats.enRetard} facture(s) en retard pour un montant total de {formatAmount(stats.montantRetard)}.
                Action immédiate requise !
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tableau des échéances */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Tableau de Bord des Échéances</h3>
          <p className="text-sm text-gray-600 mt-1">Suivi des factures fournisseurs à payer</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Urgence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facture</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseur</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date Échéance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant Restant</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInvoices.map((invoice) => {
                const urgencyLevel = getUrgencyLevel(invoice);
                return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className={`inline-flex items-center px-2.5 py-1.5 text-xs font-semibold rounded-full border ${getUrgencyColor(urgencyLevel)}`}>
                        <i className={`${getUrgencyIcon(urgencyLevel)} mr-1`}></i>
                        {urgencyLevel === 'retard' && 'RETARD'}
                        {urgencyLevel === 'urgent' && 'URGENT'}
                        {urgencyLevel === 'attention' && 'ATTENTION'}
                        {urgencyLevel === 'normal' && 'NORMAL'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{invoice.numero_facture_fournisseur}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{invoice.fournisseur.raison_sociale}</div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-sm text-gray-900">{formatDate(invoice.date_echeance)}</div>
                      <div className={`text-xs font-medium ${
                        invoice.en_retard ? 'text-red-600' :
                        invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance <= 3 ? 'text-orange-600' :
                        invoice.jours_avant_echeance !== null && invoice.jours_avant_echeance <= 7 ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {invoice.en_retard
                          ? `Retard: ${Math.abs(invoice.jours_avant_echeance || 0)} j`
                          : invoice.jours_avant_echeance !== null
                            ? `Dans ${invoice.jours_avant_echeance} j`
                            : '—'
                        }
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="text-sm font-bold text-red-600">
                        {formatAmount(invoice.montant_restant)}
                      </div>
                      {invoice.montant_paye > 0 && (
                        <div className="text-xs text-gray-500">
                          Réglé: {formatAmount(invoice.montant_paye)}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        Total: {formatAmount(invoice.total_ttc)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                        invoice.statut_paiement_label === 'Soldée' ? 'bg-green-100 text-green-800' :
                        invoice.statut_paiement_label === 'Payée partiellement' ? 'bg-orange-100 text-orange-800' :
                        invoice.statut_paiement_label === 'Impayée' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.statut_paiement_label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => onViewInvoice(invoice)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded cursor-pointer"
                          title="Voir détails"
                        >
                          <i className="ri-eye-line w-4 h-4"></i>
                        </button>
                        <button
                          onClick={() => onAddPayment(invoice)}
                          className="text-green-600 hover:text-green-900 p-1 rounded cursor-pointer"
                          title="Ajouter un règlement"
                        >
                          <i className="ri-money-euro-circle-line w-4 h-4"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedInvoices.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-calendar-check-line text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-500">
              {filterPeriod === 'all' 
                ? 'Aucune facture en attente de paiement' 
                : 'Aucune facture trouvée pour cette période'
              }
            </p>
          </div>
        )}
      </div>

      {/* Résumé par fournisseur */}
      {unpaidInvoices.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé par Fournisseur</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(
              unpaidInvoices.reduce((acc: any, invoice) => {
                const nom = invoice.fournisseur.raison_sociale;
                if (!acc[nom]) {
                  acc[nom] = { count: 0, amount: 0 };
                }
                acc[nom].count += 1;
                acc[nom].amount += invoice.montant_restant;
                return acc;
              }, {})
            ).map(([fournisseur, data]: [string, any]) => (
              <div key={fournisseur} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">{fournisseur}</h4>
                <div className="text-sm text-gray-600">
                  <div>{data.count} facture(s)</div>
                  <div className="font-semibold text-red-600">
                    {formatAmount(data.amount)} à payer
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}